import { TFunction } from 'i18next';
import type { BadgeVariant } from '@/components/ui/badge';
import { FlairIconName } from '../../../../../atomic/atoms/FlairIcon';
import {
  ExpenseGroup,
  ExpenseStatus,
  ExpenseGroupType,
  RecordTypeBaseFragment,
} from '../../../__generated__/graphql';
import type { ExpenseGroupWithRelations } from '../ExpensesTable/ExpensesTable';
import { ExpenseRecordType } from '../types';

export type UngroupedExpense = {
  Id: string;
  Name: string;
  flair__Type__c: string;
  totalAmount: number;
  startDate: Date;
  endDate: Date | null;
  flair__Status__c: ExpenseStatus;
  flair__Reimbursed__c: boolean;
  CurrencyIsoCode: string;
  employee: {
    Id: string;
    Name: string;
  };
  isGroup: boolean;
  recordType?: RecordTypeBaseFragment | null;
};

export type ExpenseItem = ExpenseGroupWithRelations | UngroupedExpense;

export const getStatusVariant = (
  status: ExpenseStatus,
  isReimbursed?: boolean,
): BadgeVariant => {
  if (isReimbursed) {
    return 'info';
  }
  switch (status) {
    case ExpenseStatus.Approved:
      return 'success';
    case ExpenseStatus.Declined:
      return 'danger';
    case ExpenseStatus.Pending:
      return 'warning';
    case ExpenseStatus.Draft:
      return 'secondary';
    case ExpenseStatus.Unknown:
      return 'danger';
    default:
      return 'secondary';
  }
};

export const getStatusLabel = (
  status: ExpenseStatus,
  t: TFunction,
  isReimbursed?: boolean,
): string => {
  if (isReimbursed) {
    return t('expenses.approvalStatus.REIMBURSED');
  }
  switch (status) {
    case ExpenseStatus.Approved:
      return t('expenses.approvalStatus.APPROVED');
    case ExpenseStatus.Declined:
      return t('expenses.approvalStatus.DECLINED');
    case ExpenseStatus.Pending:
      return t('expenses.approvalStatus.PENDING');
    case ExpenseStatus.Draft:
      return t('expenses.approvalStatus.DRAFT');
    case ExpenseStatus.Unknown:
      return t('expenses.approvalStatus.UNKNOWN');
    default:
      return t('expenses.approvalStatus.UNKNOWN');
  }
};

const getRecordTypeIcon = (recordTypeDeveloperName: string): FlairIconName => {
  switch (recordTypeDeveloperName) {
    case ExpenseRecordType.PerDiem:
      return 'cafe-outline';
    case ExpenseRecordType.Reimbursement:
      return 'expenses-outline';
    case ExpenseRecordType.Mileage:
      return 'vehicle-icon';
    default:
      return 'document-outline';
  }
};

export const getIcon = (item: ExpenseItem): FlairIconName => {
  if (item.isGroup) {
    return item.flair__Type__c === 'TRIP' ? 'sunny-icon' : 'folder-outline';
  }

  if ('recordType' in item && item.recordType?.DeveloperName) {
    return getRecordTypeIcon(item.recordType.DeveloperName);
  }

  return 'document-outline';
};

export const getTripLocation = (item: ExpenseGroup, t: TFunction): string => {
  return item.flair__Trip_Location__c
    ? item.flair__Trip_Location__c
    : t('expenses.groups.notSpecified');
};

export const isTrip = (item: ExpenseGroup): boolean => {
  return item.flair__Type__c === ExpenseGroupType.Trip;
};

export const getExpenseType = (
  item: ExpenseItem,
  childExpensesCount: number,
  t: TFunction,
): string => {
  if (!item.isGroup) return t('expenses.groups.individualExpense');

  if (childExpensesCount === 0)
    return item.flair__Type__c === ExpenseGroupType.Trip
      ? t('expenses.groups.trip')
      : t('expenses.groups.group');

  const itemText =
    childExpensesCount === 1
      ? t('expenses.groups.expense')
      : t('expenses.groups.expenses');
  return `${
    item.flair__Type__c === ExpenseGroupType.Trip
      ? t('expenses.groups.trip')
      : t('expenses.groups.group')
  } ∙ ${childExpensesCount} ${itemText}`;
};
